import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
// NOTE: not importing shadcn `Table` — its internal wrapper `<div className="overflow-auto">`
// becomes the sticky scroll-container instead of ours, which breaks
// position:sticky on <th>. We use raw <table>/<tbody>/etc. for the table
// itself but keep TableRow/TableHead/TableCell for consistent cell styling.
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Copy, Download, FilterX, Plus, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/hooks/useSession';
import { PageShell } from '@/shared/components/page';
import { getAccountsTable } from '../api';
import { CreateAccountDialog } from '../components/CreateAccountDialog';
import { AccountsColumnsMenu } from '../components/AccountsColumnsMenu';
import { AccountsColumnResizeHandle } from '../components/AccountsColumnResizeHandle';
import { useSalesManagers } from '../hooks/useSubscriptionManagers';
import { useAccountsColumnState } from '../hooks/useAccountsColumnState';
import { COLUMNS } from '../lib/accountsColumnDefs';
import { ACCOUNT_PERMISSIONS } from '../permissions';
import {
  buildServerTextFilters,
  copyToClipboard,
  exportToCSV,
  renderColumnFilter,
  SortableHeader,
  stripAccents,
} from '../lib/accountsColumnFilters';
import {
  DEFAULT_COLUMN_WIDTH,
  FILTER_STORAGE_KEY,
  SORT_STORAGE_KEY,
} from '../lib/accountsPresets';
import type { AccountTableSortKey } from '../types';

interface ServerFilters {
  accountNumberSearch?: string;
  nameSearch?: string;
  contractualManagerSearch?: string;
  salesManagerId?: string;
  contractNumberSearch?: string;
}

// One-shot fetch — server-side cap is 5000 (see routes/v4/accounts.js).
// Current account count is ~245, so this is well over the visible horizon.
const PAGE_SIZE = 5000;

export default function AccountsListPage() {
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const canCreateAccount = useHasPermission(ACCOUNT_PERMISSIONS.create);
  const { data: salesManagers = [] } = useSalesManagers();

  // ---- Sort state (persisted) ---------------------------------------------
  // Initial sort is read from localStorage so the user lands on the same view
  // after a reload. Stored as `{ key, direction }`.
  const [sortKey, setSortKey] = useState<AccountTableSortKey>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { key?: string };
        if (parsed?.key) return parsed.key as AccountTableSortKey;
      }
    } catch {
      /* ignore */
    }
    return 'name';
  });
  const [sortDirection, setSortDirection] = useState<1 | -1>(() => {
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { direction?: number };
        if (parsed?.direction === -1 || parsed?.direction === 1) return parsed.direction;
      }
    } catch {
      /* ignore */
    }
    return 1;
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        SORT_STORAGE_KEY,
        JSON.stringify({ key: sortKey, direction: sortDirection }),
      );
    } catch {
      /* ignore */
    }
  }, [sortKey, sortDirection]);

  // ---- Column-level filters ------------------------------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {
      /* ignore */
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Debounce text filters that hit the server (avoid query storm on each keystroke).
  // PAGE_SIZE=5000 still fetches the whole customer book per query key, so the
  // bulk of filtering/scrolling is client-side (filteredAccounts below); these
  // server-side filters only widen the fetched set for name/manager/etc. searches.
  const serverTextFilters = useMemo(() => buildServerTextFilters(columnFilters), [columnFilters]);
  const [debouncedServerTextFilters, setDebouncedServerTextFilters] =
    useState<ServerFilters>(() => buildServerTextFilters(columnFilters));
  const isFirstFilterRender = useRef(true);

  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedServerTextFilters(serverTextFilters);
    }, 300);
    return () => clearTimeout(timer);
  }, [serverTextFilters]);

  // ---- Column visibility / order / widths ---------------------------------
  const {
    visibleColumns,
    orderedColumns,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnOrder,
    setColumnWidth,
  } = useAccountsColumnState();

  // ---- Data fetch ----------------------------------------------------------
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      'accounts-table',
      'customer',
      'all',
      sortKey,
      sortDirection,
      debouncedServerTextFilters.accountNumberSearch ?? '',
      debouncedServerTextFilters.nameSearch ?? '',
      debouncedServerTextFilters.contractualManagerSearch ?? '',
      debouncedServerTextFilters.salesManagerId ?? '',
      debouncedServerTextFilters.contractNumberSearch ?? '',
    ],
    queryFn: () =>
      getAccountsTable({
        pageNumber: 0,
        pageLength: PAGE_SIZE,
        type: 'customer',
        excludeOrganizations: 'true',
        sortKey,
        sortDirection,
        ...debouncedServerTextFilters,
      }),
    // Cache the whole customer book 15 min so filtering/scrolling never reloads.
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });

  const accounts = data?.accounts || [];
  const total = data?.total || 0;

  // ---- Client-side filtering within the fetched set -----------------------
  const filteredAccounts = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return accounts;

    return accounts.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);
        const filterableValue = col.getFilterValue ? col.getFilterValue(item) : rawValue;

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(filterableValue || ''));
          } catch {
            return true;
          }
        }

        if (col.filterType === 'single-select') {
          return !filterValue || String(filterableValue || '') === filterValue;
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Default: case-insensitive, accent-insensitive contains.
        return stripAccents(String(rawValue ?? '').toLowerCase()).includes(
          stripAccents(filterValue.toLowerCase()),
        );
      }),
    );
  }, [accounts, columnFilters]);

  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  // ---- Handlers -----------------------------------------------------------
  const handleSort = (key: string) => {
    const typedKey = key as AccountTableSortKey;
    if (typedKey === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(typedKey);
      // Default direction = "most useful first":
      //   numeric/date columns → DESC (largest / most recent at top)
      //   text columns         → ASC  (A → Z)
      const col = COLUMNS.find((c) => c.sortKey === key);
      const startsDescending =
        col?.filterType === 'number-range'
        || col?.filterType === 'number-sum'
        || col?.filterType === 'date-range';
      setSortDirection(startsDescending ? -1 : 1);
    }
  };

  const handleCopy = () => {
    copyToClipboard(filteredAccounts, COLUMNS);
    toast.success('Copied to clipboard');
  };

  const handleExportCSV = () => {
    exportToCSV(filteredAccounts, COLUMNS, 'accounts');
    toast.success(`Exported ${filteredAccounts.length} rows`);
  };

  // Measure the filter row's actual height so the label row's `top` matches it
  // exactly — a hardcoded value drifts when the visible-column mix changes
  // (single-line filters ≈ 36px, date-range/number-range stacked inputs ≈ 58px)
  // and any mismatch leaves the label row overlapping the first body row.
  const filterRowRef = useRef<HTMLTableRowElement>(null);
  const [labelRowTop, setLabelRowTop] = useState(0);
  useLayoutEffect(() => {
    const el = filterRowRef.current;
    if (!el) return;
    const sync = () => setLabelRowTop(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const widthFor = (key: string) => columnWidths[key] ?? DEFAULT_COLUMN_WIDTH;
  // Sum of visible column widths drives the table's intrinsic width. Without
  // this, `table-layout: fixed` + `w-full` (from shadcn Table) makes the
  // browser redistribute leftover space across columns — drag-to-resize ends
  // up shifting OTHER columns instead of changing the one you grabbed.
  const totalTableWidth = visibleColumns.reduce((sum, col) => sum + widthFor(col.key), 0);

  return (
    // h-full + flex column = page fills the .admin-content area. PageShell
    // header + toolbar take their natural height; the table container claims
    // the rest via flex-1 and does its own internal scrolling. No outer page
    // scroll = sticky <th> anchors to the table div, not the viewport.
    <PageShell className="flex flex-col h-full accounts-tables-font">
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap pb-2">
        <div className="px-1 py-1 text-sm text-muted-foreground">
          {filteredAccounts.length < accounts.length
            ? `Showing ${filteredAccounts.length} of ${accounts.length} loaded`
            : `Showing ${accounts.length} of ${total}`}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveColumnFilters && (
            <Button variant="outline" size="sm" onClick={() => setColumnFilters({})}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Reload accounts from server"
          >
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <AccountsColumnsMenu
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onReorder={setColumnOrder}
          />
          {canCreateAccount && (
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add Account
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border bg-white flex-1 min-h-0 flex flex-col">
        {/* flex-1 + min-h-0 fills remaining vertical space. The inner div
            scrolls both axes; sticky <th> anchors to its top. No fixed pixel
            height — the table grows with the window. */}
        <div
          className="flex-1 min-h-0"
          style={{
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          {/* Raw <table> on purpose — see the import comment above. */}
          <table
            className="caption-bottom text-sm"
            style={{
              tableLayout: 'fixed',
              width: `${totalTableWidth}px`,
              // `collapse` is required for shadcn TableRow's `border-b` to
              // render — `separate` suppresses borders on <tr>. Sticky <th>
              // still works fine with collapse in all modern browsers.
              borderCollapse: 'collapse',
            }}
          >
            {/* colgroup is required with table-layout:fixed so widths apply to ALL rows
                (filter row, label row, body rows) — not just whichever row defines them. */}
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: `${widthFor(col.key)}px` }} />
              ))}
            </colgroup>
            {/* Sticky header — applied per <th> via INLINE STYLES because
                the TableHead component carries `position: relative` from its
                own className, which beat Tailwind sticky classes via specificity.
                Inline style always wins. Row 1 sits at top:0, row 2 underneath. */}
            <TableHeader>
              {/* ROW 1: Column filter inputs.
                  `bg-muted/30` matches SensorsTab/InvoicesTab visually. The
                  inline sticky background is the opaque equivalent of
                  `bg-muted/30` over a white card — required because sticky
                  cells need a solid bg or body rows bleed through. */}
              <TableRow ref={filterRowRef} className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter, salesManagers, filteredAccounts, {
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    background: 'rgb(251, 252, 253)',
                  }),
                )}
              </TableRow>

              {/* ROW 2: Labels + sort + resize handles. `top` matches the
                  measured filter-row height (see labelRowTop) so the label row
                  sits exactly under row 1 regardless of which column-filter
                  types are visible. */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    // Auto height + `whitespace-normal` lets long labels wrap to 2 lines
                    // when a column is narrow (e.g. "Transmitter Active" stacks as
                    // "Transmitter" / "Active"). `leading-tight` keeps the row compact.
                    className={cn(
                      'h-auto min-h-10 py-1.5 align-middle whitespace-normal leading-tight',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                    // Inline sticky overrides TableHead's default positioning.
                    // White background matches the card surface SensorsTab/etc
                    // sit on, so labels look identical across all tables.
                    style={{
                      position: 'sticky',
                      top: labelRowTop,
                      zIndex: 20,
                      background: '#ffffff',
                    }}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                    {!col.noFilter && (
                      <AccountsColumnResizeHandle
                        columnKey={col.key}
                        currentWidth={widthFor(col.key)}
                        onResize={setColumnWidth}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!isLoading && filteredAccounts.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No accounts found
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                filteredAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/accounts/${account.id}`)}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          // Match SensorsTab body styling — py-1.5/px-2 compact
                          // padding, align-top for multi-line content, and
                          // overflow-hidden + min-w-0 so cell content can't push
                          // past the colgroup-pinned column width.
                          'text-sm py-1.5 px-2 overflow-hidden align-top min-w-0',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(account)
                          : String(col.getValue(account) ?? '') || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>
      </div>

      <CreateAccountDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </PageShell>
  );
}

// Note: AccountsListPage is intentionally NOT memoized. The render cost is
// dominated by the table body (~245 rows × ~15 visible columns ≈ 3.7K cells).
// If perf becomes an issue, add @tanstack/react-virtual for body rows.
